
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Power, Unplug, Search, Cloud } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";

const formSchema = z.object({
  projectId: z.string().min(1, "Project ID is required."),
  topicId: z.string().optional(), // Topic ID will be set from the select, but keep in form
});

export type ConnectionFormValues = z.infer<typeof formSchema>;

interface Topic {
  id: string;
  name: string;
}

type ConnectionFormProps = {
  projectId: string;
  setProjectId: Dispatch<SetStateAction<string>>;
  topics: Topic[];
  selectedTopic: string | null;
  setSelectedTopic: Dispatch<SetStateAction<string | null>>;
  isSubscribed: boolean;
  isLoadingTopics: boolean;
  isLoadingSubscription: boolean;
  onFetchTopics: (projectId: string) => void;
  onConnect: (values: ConnectionFormValues) => void;
  onDisconnect: () => void;
};

export function ConnectionForm({
  projectId,
  setProjectId,
  topics,
  selectedTopic,
  setSelectedTopic,
  isSubscribed,
  isLoadingTopics,
  isLoadingSubscription,
  onFetchTopics,
  onConnect,
  onDisconnect,
}: ConnectionFormProps) {
  const form = useForm<ConnectionFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectId: projectId || "",
      topicId: selectedTopic || "",
    },
    values: { // Ensure form is reactive to prop changes
        projectId: projectId,
        topicId: selectedTopic || "",
    }
  });

  const currentProjectId = form.watch("projectId");

  const handleProjectInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue("projectId", e.target.value, { shouldValidate: true });
    setProjectId(e.target.value); // Update parent state immediately for reactivity
  };
  
  const handleFetchTopicsClick = () => {
    onFetchTopics(currentProjectId);
  };

  function onSubmit(values: ConnectionFormValues) {
     if (!selectedTopic) {
        toast({ variant: "destructive", title: "No Topic Selected", description: "Please select a topic to connect." });
        return;
     }
    onConnect({ ...values, topicId: selectedTopic });
  }


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {!isSubscribed && (
          <>
            <FormField
              control={form.control}
              name="projectId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center"><Cloud className="mr-2 h-4 w-4" />GCP Project ID</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter GCP Project ID" 
                      {...field}
                      value={currentProjectId} // Controlled by form state which is synced with parent
                      onChange={handleProjectInputChange}
                      aria-describedby="projectId-message" 
                      disabled={isLoadingTopics || isLoadingSubscription}
                    />
                  </FormControl>
                  <FormMessage id="projectId-message" />
                </FormItem>
              )}
            />
            <Button 
              type="button" 
              onClick={handleFetchTopicsClick} 
              variant="outline" 
              className="w-full"
              disabled={!currentProjectId || isLoadingTopics || isLoadingSubscription}
            >
              {isLoadingTopics ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Search />
              )}
              Fetch Topics
            </Button>

            {topics.length > 0 && (
              <FormField
                control={form.control}
                name="topicId" // This field now just reflects selectedTopic
                render={() => ( // field isn't directly used as Select manages its own value
                  <FormItem>
                    <FormLabel>Select Topic</FormLabel>
                    <Select
                      value={selectedTopic || ""}
                      onValueChange={(value) => setSelectedTopic(value)}
                      disabled={isLoadingTopics || isLoadingSubscription}
                    >
                      <FormControl>
                        <SelectTrigger aria-describedby="topicId-message">
                          <SelectValue placeholder="Select a topic" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {topics.map((topic) => (
                          <SelectItem key={topic.id} value={topic.id}>
                            {topic.name} ({topic.id.startsWith('projects/') ? 'Full ID' : topic.id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage id="topicId-message" />
                  </FormItem>
                )}
              />
            )}
          </>
        )}

        {isSubscribed ? (
          <Button type="button" onClick={onDisconnect} variant="destructive" className="w-full" disabled={isLoadingSubscription}>
            {isLoadingSubscription ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Unplug />
            )}
            Disconnect
          </Button>
        ) : (
          <Button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90" 
            disabled={!selectedTopic || isLoadingSubscription || isLoadingTopics || topics.length === 0}
          >
            {isLoadingSubscription ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Power />
            )}
            Connect to Topic
          </Button>
        )}
      </form>
    </Form>
  );
}

// Helper for ConnectionForm to display toast. Could be moved to page.tsx if preferred.
import { toast } from "@/hooks/use-toast"; 
